import { EntityTaxRate } from './entity';
import { NatureEnum } from './nature';
import { UserOverview } from './user';
import { WorksheetStatusEnum } from './worksheet';

export enum JournalProviderEnum {
    XERO = 'XERO',
}

export enum JournalStatusEnum {
    DRAFT = 'DRAFT',
    VOIDED = 'VOIDED',
    POSTED = 'POSTED',
    DELETED = 'DELETED',
    ARCHIVED = 'ARCHIVED',
}

export enum JournalTaxTypeEnum {
    NO_TAX = 'NO_TAX',
    INCLUSIVE = 'INCLUSIVE',
    EXCLUSIVE = 'EXCLUSIVE',
}

export type JournalMetadata = {
    metadata: { draft: number; posted: number; voided: number; archived: number };
};

export type JournalOverview = {
    id: string;
    firmId: string;
    workpaperId: string;
    providerId?: string | null;
    provider?: JournalProviderEnum | null;
    taxType?: JournalTaxTypeEnum | null;
    narration?: string;
    date?: Date;
    reference: number;
    status: JournalStatusEnum;
    accounts: string[];
    lines: string[];
    creditTotal?: number;
    debitTotal?: number;
    createdAt: Date;
    updatedAt: Date;
    createdBy: UserOverview;
};

export type JournalLine = {
    id: string;
    sequence: number;
    description?: string;
    debit?: number;
    credit?: number;
    lineAmount?: number;
    taxAmount?: number;
    lineTotal?: number;
    lineType?: NatureEnum;
    createdAt: Date;
    updatedAt: Date;
    taxRate?: EntityTaxRate | null;
    account: { id: string; code: string; description: string; class: string; entityAccountId: string };
    worksheet?: { id: string; status: WorksheetStatusEnum } | null;
};

export type Journal = {
    id: string;
    firmId: string;
    workpaperId: string;
    providerId?: string | null;
    provider?: JournalProviderEnum | null;
    taxType: JournalTaxTypeEnum;
    narration?: string;
    date?: Date;
    reference: number;
    status: JournalStatusEnum;
    lines: JournalLine[];
    subtotal?: { debit?: number; credit?: number };
    taxTotal?: { debit?: number; credit?: number };
    total?: { debit?: number; credit?: number };
    createdAt: Date;
    updatedAt: Date;
    createdBy: UserOverview;
};

export type JournalLinesFields = {
    id: string;
    description: string | null;
    account: string | null;
    debit: number | null;
    credit: number | null;
    amountEditable: boolean;
    taxRate?: { id?: string; name?: string; rate?: number } | null;
    sequence?: number;
};

export type NewOrEditJournalFields = {
    date: string | null;
    narration: string | null;
    lines: JournalLinesFields[];
    taxType?: JournalTaxTypeEnum | null;
    debitTotal?: number;
    creditTotal?: number;
    debitSubtotal?: number;
    creditSubtotal?: number;
    debitTaxTotal?: number;
    creditTaxTotal?: number;
    valueOutBy?: number;
};

export type NewJournalReq = {
    date?: string;
    status: JournalStatusEnum;
    narration?: string;
    taxType?: JournalTaxTypeEnum;
    debitTotal?: number;
    creditTotal?: number;
    lines: {
        id?: string;
        description?: string;
        account?: string;
        taxRate?: string;
        lineAmount?: number | null;
        lineType?: NatureEnum;
    }[];
};

export type UpdateJournalLineReq = {
    id?: string;
    fields: {
        description?: string | null;
        account?: string | null;
        sequence?: number;
        taxRate?: string | null;
        lineAmount?: number | null;
        lineType?: NatureEnum;
    } | null;
};

export type UpdateJournalReq = {
    id: string;
    date?: string | null;
    status?: JournalStatusEnum;
    narration?: string | null;
    debitTotal?: number;
    creditTotal?: number;
    taxType?: JournalTaxTypeEnum | null;
    lines?: UpdateJournalLineReq[];
};

export type JournalTotals = {
    debitTotal: number;
    creditTotal: number;
    debitSubtotal?: number;
    creditSubtotal?: number;
    debitTaxTotal?: number;
    creditTaxTotal?: number;
};

export type JournalStatistics = {
    draft: number;
    posted: number;
    voided: number;
};
